<!--
  @component PreviewPlanIO — P2 effecting lens (u3 plan-before-apply).
  chain:   ctx.deployment.dispatch("previewRun"|"previewOnly"|"previewFork", …)
           → this → ctx.previewPlan
  effects: three dispatches (preview, previewOnly, previewFork); the
           daemon returns a diff WITHOUT applying; gate runs before
           any real change. Unwired ⇒ NotWiredError (FACE-LEDGER).
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import { PREVIEW_PLAN_IO_KEY } from "./deployment-io.js";
  import type { PreviewPlanIOContext } from "./deployment-io.js";

  export { PREVIEW_PLAN_IO_KEY };
  export type { PreviewPlanIOContext };

  export function usePreviewPlanIO(): PreviewPlanIOContext {
    const ctx = getContext<PreviewPlanIOContext | undefined>(
      PREVIEW_PLAN_IO_KEY,
    );
    if (!ctx) {
      throw new Error(
        "usePreviewPlanIO() called outside of a PreviewPlanIO tree.",
      );
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import type { SupplyRunSpec } from "./deployment-io.js";

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastDiff: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchPreview(
    op: "previewRun" | "previewOnly" | "previewFork",
    payload: unknown,
  ): Promise<unknown> {
    inFlight = true;
    lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastDiff = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally {
      inFlight = false;
    }
  }

  const ctx: PreviewPlanIOContext = {
    get inFlight() {
      return inFlight;
    },
    get lastDiff() {
      return lastDiff;
    },
    get lastError() {
      return lastError;
    },
    get history() {
      return parent.stream.filter(
        (e) =>
          e.op === "previewRun" ||
          e.op === "previewOnly" ||
          e.op === "previewFork",
      );
    },
    preview: (spec: SupplyRunSpec) => dispatchPreview("previewRun", spec),
    previewOnly: (spec: SupplyRunSpec) =>
      dispatchPreview("previewOnly", spec),
    previewFork: (spec, fork) =>
      dispatchPreview("previewFork", { spec, fork }),
  } satisfies PreviewPlanIOContext;

  setContext(PREVIEW_PLAN_IO_KEY, ctx);
</script>

{@render children?.()}
